import { Component, Injector, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';

import { Subject, takeUntil } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';

import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { DragDropComponent } from '@components/drag-drop/drag-drop.component';
import { EmailApi } from '@api/email.api';
import { EmailService } from '@services/email.service';
import { AuthService } from '@services/auth.service';
import { UserService } from '@services/user.service';
import { FormValidationService } from '@services/form-validation.service';
import { NotificationService } from '@services/notification.service';
import { emptyValueValidator } from '@validators/empty-value.validator';
import { USER_ROLES } from '@configs/role-matrix';
import { EmailData, EmailTemplateData, SearchEmailTemplate } from '@models/email';
import { DialogData } from '@models/dialog';
import { User } from '@models/user';

@Component({
  selector: 'app-email-dialog',
  templateUrl: './email-dialog.component.html'
})
export class EmailDialogComponent implements OnInit, OnDestroy {
  @ViewChild(DragDropComponent) private dragAndDropComp!: DragDropComponent;

  public form: UntypedFormGroup;

  public get templateControl(): UntypedFormControl {
    return <UntypedFormControl>this.form.get('template');
  }

  public get subjectControl(): UntypedFormControl {
    return <UntypedFormControl>this.form.get('subject');
  }

  public header: string = 'Send Email';

  public companyId!: string;

  public sendToClientAndEmployee: boolean;

  public userRoles: typeof USER_ROLES = USER_ROLES;
  public currentUserRole: string;

  public templateId!: string;

  public data: DialogData;
  public api: EmailApi;
  public service: EmailService;

  protected injector: Injector;
  protected authService: AuthService;

  protected readonly destroy$: Subject<void> = new Subject();

  protected selectedTemplateData: EmailTemplateData | null = null;

  protected files: Array<File> = [];

  private dialogRef: MatDialogRef<EmailDialogComponent>;
  private formBuilder: UntypedFormBuilder;
  private userService: UserService;
  private validationService: FormValidationService;
  private notificationService: NotificationService;

  constructor(injector: Injector) {
    this.injector = injector;

    this.data = this.injector.get(MAT_DIALOG_DATA);
    this.api = this.injector.get(EmailApi);
    this.service = this.injector.get(EmailService);
    this.dialogRef = this.injector.get(MatDialogRef<EmailDialogComponent>);
    this.formBuilder = this.injector.get(UntypedFormBuilder);
    this.authService = this.injector.get(AuthService);
    this.userService = this.injector.get(UserService);
    this.validationService = this.injector.get(FormValidationService);
    this.notificationService = this.injector.get(NotificationService);

    this.form = this.createForm();
    this.sendToClientAndEmployee = !this.authService.isClient();
    this.currentUserRole = this.authService.user.role;
  }

  ngOnInit(): void {
    this.companyId = this.data.companyId;

    this.templateControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        filter<SearchEmailTemplate>(Boolean),
        distinctUntilChanged()
      )
      .subscribe((value: SearchEmailTemplate): void => this.onTemplateChange(value));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onSendBtnClick(): void {
    this.sendEmail({
      formValue: this.form.getRawValue(),
      templateData: this.selectedTemplateData
    });
  }

  public onFileDropped(files: Array<File>): void {
    this.files = files;
    this.form.markAsDirty();
  }

  public getErrorMsg(control: UntypedFormControl, controlName: string): string {
    return this.validationService.getControlErrorMessage(control, controlName);
  }

  protected onEmailSent(res: any): void {
    if (res.formErrors.length) {
      this.notificationService.openErrorNotification(res.formErrors[0]);
    } else {
      this.dialogRef.close(true);
    }
  }

  protected onTemplateChange(template: SearchEmailTemplate): void {
    this.templateId = template.id;

    this.api.getTemplateData(this.templateId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (data: EmailTemplateData): void => this.onGetTemplateData(data),
        (error: string | Error): void => { }
      );
  }

  protected onGetTemplateData(data: EmailTemplateData): void {
    this.selectedTemplateData = data;

    this.form.patchValue({
      from: this.getFromUser(data.from),
      to: data.to || '',
      cc: data.cc,
      bcc: data.bcc,
      subject: this.createSubject(data.subject),
      body_html: data.body_html
    });
  }

  protected sendEmail(data: EmailData): void {
    this.api.sendEmail(
      this.service.prepareEmail(data),
      this.files
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res: any): void => this.onEmailSent(res),
        (error: string | Error): void => { }
      );
  }

  protected createSubject(templateName: string = ''): string {
    return templateName;
  }

  protected getFromUser(user?: User): User {
    const from: User = user || this.data.from;

    return this.authService.isAccountant()
      ? this.userService.createAccountant(from)
      : from;
  }

  protected getAttachedFileIds(): Array<string> {
    return [];
  }

  private createForm(): UntypedFormGroup {
    const requiredValidator: ValidatorFn = Validators.required;

    return this.formBuilder.group({
      template: null,
      from: [this.getFromUser(), requiredValidator],
      to: [null, requiredValidator],
      cc: null,
      bcc: null,
      subject: [this.data.order?.full_address, [requiredValidator, emptyValueValidator]],
      body_html: null
    });
  }
}
